// CB:BEGIN project-cancel-modal
import React, { useState } from 'react';
import { useAuthFetch } from '../lib/useAuthFetch';

interface Props {
  projectId: string;
  projectAddress?: string;
  onCancelled: () => void;
  onClose: () => void;
}

const CANCEL_REASONS = [
  'Found a contractor outside CoreBids',
  'Project no longer needed',
  'Not happy with the bids received',
  'Budget constraints',
  'Project timeline changed',
  'Other',
];

const ProjectCancelModal: React.FC<Props> = ({ projectId, projectAddress, onCancelled, onClose }) => {
  const authFetch = useAuthFetch();
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const finalReason = reason === 'Other' ? customReason.trim() : reason;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!finalReason) {
      setError('Please select or enter a cancellation reason.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await authFetch(`/api/projects/${projectId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: finalReason }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.error === 'CANCELLATION_LOCKED') {
          setError(data.message || 'This project cannot be cancelled after work has started.');
        } else if (data.error === 'PROJECT_ALREADY_CANCELLED') {
          setError('This project has already been cancelled.');
        } else {
          setError(data.error || data.message || 'Failed to cancel project. Please try again.');
        }
        return;
      }
      onCancelled();
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 16,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 24,
          maxWidth: 480,
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#111827' }}>
          Cancel Project
        </h2>
        {projectAddress && (
          <p style={{ margin: '0 0 16px', fontSize: 14, color: '#6b7280' }}>{projectAddress}</p>
        )}
        <p style={{ margin: '0 0 16px', fontSize: 14, color: '#374151' }}>
          Are you sure you want to cancel this project? This action cannot be undone.
          If you have an active escrow, contact support before cancelling.
        </p>

        <form onSubmit={handleSubmit}>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 8, color: '#374151' }}>
            Reason for cancellation
          </label>
          <select
            value={reason}
            onChange={e => { setReason(e.target.value); setError(null); }}
            required
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              color: '#374151',
              background: '#fff',
              marginBottom: 12,
              boxSizing: 'border-box',
            }}
          >
            <option value="">Select a reason...</option>
            {CANCEL_REASONS.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>

          {reason === 'Other' && (
            <textarea
              value={customReason}
              onChange={e => { setCustomReason(e.target.value); setError(null); }}
              placeholder="Please describe your reason..."
              required
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                color: '#374151',
                marginBottom: 12,
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
          )}

          {error && (
            <div
              style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 6,
                padding: '10px 12px',
                fontSize: 14,
                color: '#dc2626',
                marginBottom: 12,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#374151',
                fontSize: 14,
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              Keep Project
            </button>
            <button
              type="submit"
              disabled={submitting || !finalReason}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                background: submitting || !finalReason ? '#fca5a5' : '#dc2626',
                color: '#fff',
                fontSize: 14,
                cursor: submitting || !finalReason ? 'not-allowed' : 'pointer',
                fontWeight: 600,
              }}
            >
              {submitting ? 'Cancelling...' : 'Cancel Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectCancelModal;
// CB:END project-cancel-modal
